import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { getDemandAdjustments } from "../../services";
import {
  DemandAdjustment,
  DemandAdjustmentStatus,
  DemandAdjustmentType,
} from "../../types/demand-factor.type";
import { ALL, API_DATE_FORMAT } from "./constants";

const DEFAULT_PAGE_SIZE = 10;

export interface DemandFactorFilters {
  /** `branch_code`, or ALL. Only head office and master can set it. */
  branchCode: string;
  type: DemandAdjustmentType | typeof ALL;
  status: DemandAdjustmentStatus | typeof ALL;
  /** Rows in effect on this day. */
  date: Dayjs | null;
}

export const INITIAL_FILTERS: DemandFactorFilters = {
  branchCode: ALL,
  type: ALL,
  status: ALL,
  date: null,
};

export interface DemandAdjustmentList {
  rows: DemandAdjustment[];
  count: number;
  /** Null until the first answer arrives, so no type is flagged as disabled meanwhile. */
  enabledTypes: DemandAdjustmentType[] | null;
  loading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
}

/** One server-paginated page of the Demand Factor list; `reloadKey` refetches it. */
export function useDemandAdjustmentList(
  filters: DemandFactorFilters,
  reloadKey: number,
): DemandAdjustmentList {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState<DemandAdjustment[]>([]);
  const [count, setCount] = useState<number>(0);
  const [enabledTypes, setEnabledTypes] = useState<DemandAdjustmentType[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const branchCode = filters.branchCode === ALL ? undefined : filters.branchCode;
  const type = filters.type === ALL ? undefined : filters.type;
  const status = filters.status === ALL ? undefined : filters.status;
  const date = filters.date?.isValid() ? filters.date.format(API_DATE_FORMAT) : undefined;

  // A filter change restarts the list from the first page, in the same render as the change,
  // so the fetch below runs once with the new page instead of once per page value.
  const filterSignature = JSON.stringify({ branchCode, type, status, date });
  const [appliedFilterSignature, setAppliedFilterSignature] =
    useState<string>(filterSignature);
  if (filterSignature !== appliedFilterSignature) {
    setAppliedFilterSignature(filterSignature);
    setPage(1);
  }

  useEffect(() => {
    // A slower earlier response must not overwrite the rows of a later request.
    let ignored = false;

    const loadPage = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getDemandAdjustments({
          page,
          limit,
          branch_code: branchCode,
          type,
          status,
          date,
        });
        if (ignored) return;
        setRows(data?.rows ?? []);
        setCount(data?.count ?? 0);
        setEnabledTypes(data?.enabled_types ?? null);
      } catch {
        // The axios interceptor already reported the failure; an empty page is shown.
        if (ignored) return;
        setRows([]);
        setCount(0);
      } finally {
        if (!ignored) setLoading(false);
      }
    };

    loadPage();

    return () => {
      ignored = true;
    };
  }, [page, limit, branchCode, type, status, date, reloadKey]);

  return { rows, count, enabledTypes, loading, page, setPage, limit, setLimit };
}
